// shared-lib/cabinet-runs/wallFrame.js
//
// The wall is the durable frame; a run's params are metres along it from
// `alignment.bottomLeft`. Walls get moved, merged, split and re-wound by
// edits underneath runs that were drawn against the old geometry
// (cabinet-run model doc §3.2). A reprocess is no longer one of them: it
// keeps no run at all (user ruling 2026-10-23).
//
// So each stored segment carries the wall frame it was written against
// (`wallFrame: { a, b }`, the endpoints — not a hash: endpoints give the
// repair transform), and every read reconciles lazily:
//
//   same endpoints                 → untouched
//   same line, shifted / reversed  → REBASE: p' = param on the new wall of
//                                    the point the old frame put at p
//                                    (one formula covers offset AND mirror)
//   different line                 → ORPHAN: keep the segment, flag it,
//                                    the fill skips it and warns
//
// A caller that already KNOWS the wall moved (a wall-move regen) passes
// `{ project: true }` and gets the rebase for the third case too: the params
// project onto the new line rather than being read as an orphan.
//
// Lazy on read needs no cooperation from any wall mutator; an eager hook
// that misses one site fails silently. Pure.

const {
  getWallAxes,
  worldToWallParam,
} = require('../kitchen-assembly/wallMath');

const SAME_LINE_TOL = 0.05; // m — perpendicular drift beyond this is a new wall
const SAME_POINT_TOL = 1e-3;

function frameOf(wall) {
  const bl = wall?.alignment?.bottomLeft;
  const br = wall?.alignment?.bottomRight;
  if (!bl || !br) return null;
  return { a: { x: bl.x, z: bl.z }, b: { x: br.x, z: br.z } };
}

function samePoint(p, q) {
  return Math.hypot(p.x - q.x, p.z - q.z) <= SAME_POINT_TOL;
}

function perpDistance(wall, p) {
  const ax = getWallAxes(wall);
  if (!ax) return Infinity;
  return Math.abs(
    (p.x - ax.start.x) * ax.wallNormal.x + (p.z - ax.start.z) * ax.wallNormal.z
  );
}

/** World point the OLD frame put at param p. */
function pointAtParam(frame, p) {
  const dx = frame.b.x - frame.a.x;
  const dz = frame.b.z - frame.a.z;
  const len = Math.hypot(dx, dz) || 1;
  return { x: frame.a.x + (p / len) * dx, z: frame.a.z + (p / len) * dz };
}

/**
 * @param {Object} frame — the stored `wallFrame`
 * @param {Object} wall — the CURRENT engine wall (`alignment`)
 * @param {{ project?: boolean }} [opts] — `project` skips the same-line test
 *   and always rebases by PROJECTION. For a caller that already knows the wall
 *   moved (a wall-move regen re-expressing params measured on the previous
 *   wall), "different line" is not evidence of an orphan: it is the move. A
 *   wall translated perpendicular to itself keeps its params; a wall grown at
 *   its start shifts them by the growth.
 * @returns {{ action: 'none'|'rebase'|'orphan', map?: (p:number)=>number }}
 */
function reconcileFrame(frame, wall, opts = {}) {
  const cur = frameOf(wall);
  if (!frame || !cur) return { action: 'none' };
  if (samePoint(frame.a, cur.a) && samePoint(frame.b, cur.b)) {
    return { action: 'none' };
  }
  if (
    !opts.project &&
    (perpDistance(wall, frame.a) > SAME_LINE_TOL ||
      perpDistance(wall, frame.b) > SAME_LINE_TOL)
  ) {
    return { action: 'orphan' };
  }
  return {
    action: 'rebase',
    map: (p) => worldToWallParam(wall, pointAtParam(frame, p)),
  };
}

/**
 * Reconcile every wall-backed segment of a STORED run against the walls it
 * resolves to today. `resolveWall(seg)` returns the current engine wall for
 * a stored segment (or null when the id is gone). Params on the segment,
 * on its bays and on its bay overrides are mapped together.
 *
 * `opts` is passed through to `reconcileFrame` (see its `project` mode).
 *
 * @returns {{ run, actions: [{ segmentIndex, action }] }}
 */
function reconcileRunFrames(storedRun, resolveWall, opts = {}) {
  const actions = [];
  const segMaps = new Map();
  const segments = (storedRun.segments || []).map((s, i) => {
    if (s.freestanding) return s;
    const wall = resolveWall(s);
    if (!wall) {
      actions.push({ segmentIndex: i, action: 'missing' });
      return { ...s, orphaned: true };
    }
    if (!s.wallFrame) return s; // never stamped: nothing to compare against
    const r = reconcileFrame(s.wallFrame, wall, opts);
    actions.push({ segmentIndex: i, action: r.action });
    if (r.action === 'orphan') return { ...s, orphaned: true };
    // 'none' — the wall is exactly as the segment remembers it. If the segment
    // arrived carrying a STALE orphan verdict (persisted before that flag was
    // kept out of the stored shape, or set while the wall was briefly gone),
    // this is the only place that can clear it: the rebase branch below never
    // runs for an unchanged wall.
    if (r.action !== 'rebase')
      return s.orphaned ? { ...s, orphaned: false } : s;
    segMaps.set(i, r.map);
    const p0 = r.map(s.startParam);
    const p1 = r.map(s.endParam);
    // Direction is the RUN's property, not the wall's: a corner-joined
    // reversed leg (cornerJoin.reverseRun) encodes corner adjacency in its
    // param order, and min/max-normalising erased it — after a rebase
    // `sharedCornerPoint` missed the vertex and the joined run silently
    // un-cornered (#1524). Magnitudes normalise; stored order survives.
    const lo = Math.min(p0, p1);
    const hi = Math.max(p0, p1);
    const reversedStored = s.startParam > s.endParam;
    return {
      ...s,
      startParam: reversedStored ? hi : lo,
      endParam: reversedStored ? lo : hi,
      wallFrame: frameOf(wall),
      orphaned: false,
    };
  });
  if (!segMaps.size) {
    const changed = segments.some((s, i) => s !== storedRun.segments[i]);
    return { run: changed ? { ...storedRun, segments } : storedRun, actions };
  }
  const mapParam = (segIndex, p) => {
    const m = segMaps.get(segIndex ?? 0);
    return m && p != null ? m(p) : p;
  };
  // A vanity sink bay's bowl seat (`applianceParam`) is a param on the same
  // wall: left in the old frame, the next solve seated the bowl a wall-edit's
  // distance from its cabinet (12" for a wall lengthened 12" at its start,
  // 2026-11-02).
  const bays = (storedRun.bays || []).map((b) =>
    segMaps.has(b.segmentIndex ?? 0)
      ? {
          ...b,
          anchorParam: mapParam(b.segmentIndex, b.anchorParam),
          ...(Number.isFinite(b.applianceParam)
            ? { applianceParam: mapParam(b.segmentIndex, b.applianceParam) }
            : {}),
        }
      : b
  );
  // …and so is where the scan put each bowl (`run.measured.basins`), which
  // "As built" restores: on the segment of the bay holding that bowl.
  const measured = remapMeasuredBasins(storedRun, mapParam, segMaps);
  const appliances = (storedRun.appliances || []).map((a) =>
    segMaps.has(a.segmentIndex ?? 0)
      ? { ...a, centerParam: mapParam(a.segmentIndex, a.centerParam) }
      : a
  );
  const bayOverrides = (storedRun.bayOverrides || []).map((o) =>
    o.anchorParam != null && segMaps.has(o.segmentIndex ?? 0)
      ? { ...o, anchorParam: mapParam(o.segmentIndex, o.anchorParam) }
      : o
  );
  return {
    run: {
      ...storedRun,
      segments,
      ...(storedRun.bays ? { bays } : {}),
      ...(storedRun.appliances ? { appliances } : {}),
      ...(storedRun.bayOverrides ? { bayOverrides } : {}),
      ...(measured ? { measured } : {}),
    },
    actions,
  };
}

/**
 * `run.measured` with each recorded bowl's `centerParam` mapped into the new
 * frame of its bay's segment, or null when there is nothing to map.
 */
function remapMeasuredBasins(storedRun, mapParam, segMaps) {
  const basins = storedRun.measured?.basins;
  if (!Array.isArray(basins) || !basins.length) return null;
  const segOf = (id) =>
    (storedRun.bays || []).find((b) => b?.applianceId === id)?.segmentIndex ??
    0;
  return {
    ...storedRun.measured,
    basins: basins.map((basin) =>
      Number.isFinite(basin?.centerParam) && segMaps.has(segOf(basin.id))
        ? {
            ...basin,
            centerParam: mapParam(segOf(basin.id), basin.centerParam),
          }
        : basin
    ),
  };
}

/** Stamp each wall-backed stored segment with the frame of its resolved wall. */
function stampRunFrames(storedRun, resolveWall) {
  let changed = false;
  const segments = (storedRun.segments || []).map((s) => {
    if (s.freestanding) return s;
    const wall = resolveWall(s);
    const frame = frameOf(wall);
    if (!frame) return s;
    if (
      s.wallFrame &&
      samePoint(s.wallFrame.a, frame.a) &&
      samePoint(s.wallFrame.b, frame.b)
    )
      return s;
    changed = true;
    return { ...s, wallFrame: frame };
  });
  return changed ? { ...storedRun, segments } : storedRun;
}

/**
 * Re-express a stored run's params — measured in `fromWall(seg)`'s frame — on
 * `toWall(seg)`, and stamp the target frame. The general form of the read-time
 * reconcile: `reconcileRunFrames` rebases from a segment's STORED frame; this
 * lets a caller say which frame the params were measured in (the generator's
 * kitchen-clipped wall, a legacy structure-wall face) and where they go (the
 * raw room wall). Pure.
 *
 * When `fromWall` yields nothing but the segment already carries a `wallFrame`,
 * that stored stamp is used instead — a legacy stamp is a real measurement
 * frame, and falling back to it is the point of the lazy reconcile.
 *
 * Actions: `'none'` / `'rebase'` as usual; `'orphan'` when the target is on a
 * different line; `'missing'` when the target wall could not be resolved OR the
 * frame the params were measured in is unknown (no `fromWall`, no stored
 * stamp) — nothing can be rebased or even checked for such a segment, so it is
 * left exactly as it came in rather than stamped as if it had been. A caller
 * that sees an `orphan` or `missing` action should keep its original run: half
 * a migration is worse than none.
 *
 * @param {Object} storedRun
 * @param {(seg) => Object|null} fromWall wall (anything with `alignment`) the params were measured on
 * @param {(seg) => Object|null} toWall wall to express them on
 * @param {{ project?: boolean }} [opts] passed to `reconcileFrame` — `project`
 *   rebases a target on a different line instead of orphaning it.
 * @returns {{ run: Object, actions: [{ segmentIndex, action }] }}
 */
function rebaseRunFrames(storedRun, fromWall, toWall, opts = {}) {
  const originals = storedRun.segments || [];
  // Indices of the wall-backed segments whose measurement frame is UNKNOWN.
  // By index, not by reference: the reconcile can replace the object.
  const unframed = new Set();
  const framed = {
    ...storedRun,
    segments: originals.map((s, i) => {
      if (s.freestanding) return s;
      const frame = frameOf(fromWall(s));
      if (frame) return { ...s, wallFrame: frame };
      // No `fromWall`, but the segment carries its own stamp: fall back to that
      // stored frame — deliberate, and what `reconcileRunFrames` then rebases
      // from.
      if (s.wallFrame) return s;
      unframed.add(i);
      return s;
    }),
  };
  const { run, actions } = reconcileRunFrames(framed, toWall, opts);
  if (!unframed.size) return { run: stampRunFrames(run, toWall), actions };

  // The reconcile had no frame to work from at these indices, so whatever it
  // did there is not an answer about THIS segment — it either skipped it in
  // silence or, when the target failed to resolve too, reported its own
  // `missing` and flagged a copy orphaned. Drop both, restore the original,
  // and report the one thing that is true: the measurement frame is unknown.
  const segments = run.segments.map((s, i) =>
    unframed.has(i) ? originals[i] : s
  );
  const skip = new Set([...unframed].map((i) => originals[i]));
  const restored = { ...run, segments };
  return {
    // `stampRunFrames` leaves a segment untouched when its wall resolves to
    // null, which is how the unframed ones keep their original identity.
    run: stampRunFrames(restored, (s) => (skip.has(s) ? null : toWall(s))),
    actions: [
      ...actions.filter((a) => !unframed.has(a.segmentIndex)),
      ...[...unframed].map((i) => ({ segmentIndex: i, action: 'missing' })),
    ].sort((a, b) => a.segmentIndex - b.segmentIndex),
  };
}

module.exports = {
  frameOf,
  perpDistance,
  rebaseRunFrames,
  reconcileFrame,
  reconcileRunFrames,
  stampRunFrames,
  SAME_LINE_TOL,
};
